import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import client from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { InflowProductPicker } from "@/components/InflowProductPicker";
import { Search, Plus, MapPin, Package, Boxes, Pencil, Trash2, Loader2, Camera, X } from "lucide-react";

const empty = { warehouse_code: "SH", is_loose: false, location: "", product_name: "", sku: "", qty: "", notes: "", photos: [], inflow_product_id: null, inflow_product_name: null };

// Tappable SKU pill showing live inFlow stock for catalogue-linked placements.
function StockLevelBadge({ sku, productId }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["inflow-stock", productId],
    queryFn: async () => (await client.get(`/inflow/products/${productId}/stock`)).data,
    enabled: !!productId,
    staleTime: 60_000,
    retry: false,
  });
  const st = data?.status;
  const tone = st === "out" ? { bg: "#EF444422", fg: "#B91C1C" }
    : st === "low" ? { bg: "#F5A62322", fg: "#B4791A" }
    : st === "in" ? { bg: "#05966922", fg: "#059669" }
    : { bg: "#E2E8F0", fg: "#475569" };
  const label = isLoading ? null : isError || !data ? "stock n/a" : st === "out" ? "Out of stock" : `${data.available} available`;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" data-testid={`stock-level-${productId}`}
          className="mt-0.5 inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-mono text-[11px] font-bold transition-transform hover:scale-[1.03]"
          style={{ background: tone.bg, color: tone.fg }}>
          <Boxes className="h-3 w-3" />{sku}
          {isLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <span className="font-sans font-semibold"> · {label}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-56" align="start" data-testid={`stock-level-popover-${productId}`}>
        <div className="text-sm font-bold text-ink">{data?.name || sku}</div>
        {data?.sku && <div className="font-mono text-xs text-muted-foreground">{data.sku}</div>}
        {isError || !data ? (
          <p className="mt-2 text-xs text-muted-foreground">Live stock isn't available right now.</p>
        ) : (
          <div className="mt-2 space-y-1 text-xs">
            {[["On hand", data.on_hand], ["Available", data.available], ["On order", data.on_order], ["Reserved", data.reserved]].map(([k, v]) => (
              <div key={k} className="flex items-center justify-between"><span className="text-muted-foreground">{k}</span><span className="font-bold text-ink">{v}</span></div>
            ))}
          </div>
        )}
        <p className="mt-2 text-[10px] uppercase tracking-wide text-muted-foreground">Live from inFlow</p>
      </PopoverContent>
    </Popover>
  );
}

// Authenticated stock photo thumbnail. Click opens the full image in a new tab.
function StockPhoto({ placementId, photo, onDelete }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let obj = null, active = true;
    client.get(`/stock/placements/${placementId}/photos/${photo.id}`, { responseType: "blob" })
      .then((res) => { obj = window.URL.createObjectURL(res.data); if (active) setUrl(obj); })
      .catch(() => {});
    return () => { active = false; if (obj) window.URL.revokeObjectURL(obj); };
  }, [placementId, photo.id]);
  return (
    <div className="relative h-16 w-16 shrink-0 overflow-hidden rounded-lg border border-slate-200 bg-slate-50" data-testid={`stock-photo-${photo.id}`}>
      {url
        ? <img src={url} alt={photo.filename || "stock"} onClick={() => window.open(url, "_blank")} className="h-full w-full cursor-zoom-in object-cover" />
        : <div className="grid h-full w-full place-items-center"><Loader2 className="h-4 w-4 animate-spin text-slate-300" /></div>}
      {onDelete && (
        <button type="button" onClick={() => onDelete(photo.id)} data-testid={`stock-photo-del-${photo.id}`}
          className="absolute right-0.5 top-0.5 grid h-5 w-5 place-items-center rounded-full bg-black/55 text-white hover:bg-error">
          <X className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}

export default function StockLocations() {
  const [whs, setWhs] = useState([]);
  const [wh, setWh] = useState("all");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dlg, setDlg] = useState(null); // form object or null
  const [saving, setSaving] = useState(false);
  const photoRef = useRef(null);

  useEffect(() => { client.get("/stock/warehouses").then(({ data }) => setWhs(data)).catch(() => {}); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (wh !== "all") params.warehouse = wh;
      if (q.trim()) params.q = q.trim();
      const { data } = await client.get("/stock/placements", { params });
      setRows(data);
    } catch { toast.error("Couldn't load stock"); } finally { setLoading(false); }
  };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [wh, q]);

  const curWh = useMemo(() => whs.find((w) => w.code === (dlg?.warehouse_code)), [whs, dlg]);
  const locOptions = useMemo(() => {
    if (!curWh) return [];
    return dlg?.is_loose ? curWh.loose_zones : curWh.bay_codes;
  }, [curWh, dlg?.is_loose]);

  const save = async () => {
    if (!dlg.product_name.trim()) { toast.error("Add a product / description"); return; }
    if (!dlg.location) { toast.error("Pick a bay or loose zone"); return; }
    setSaving(true);
    try {
      if (dlg.id) await client.patch(`/stock/placements/${dlg.id}`, dlg);
      else await client.post("/stock/placements", dlg);
      toast.success(dlg.id ? "Stock updated" : "Stock logged");
      setDlg(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save"); } finally { setSaving(false); }
  };
  const del = async (r) => {
    if (!window.confirm(`Remove "${r.product_name}" from ${r.location}?`)) return;
    try { await client.delete(`/stock/placements/${r.id}`); toast.success("Removed"); load(); }
    catch { toast.error("Couldn't remove"); }
  };
  const uploadPhoto = async (file) => {
    if (!dlg?.id || !file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await client.post(`/stock/placements/${dlg.id}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDlg((d) => ({ ...d, photos: [...(d.photos || []), data] }));
      toast.success("Photo added"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't upload photo"); }
  };
  const deletePhoto = async (photoId) => {
    try {
      await client.delete(`/stock/placements/${dlg.id}/photos/${photoId}`);
      setDlg((d) => ({ ...d, photos: (d.photos || []).filter((p) => p.id !== photoId) }));
      load();
    } catch { toast.error("Couldn't remove photo"); }
  };

  return (
    <div className="mx-auto max-w-4xl" data-testid="stock-locations-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-ink">Locate Stock</h1>
          <p className="mt-1 text-sm text-muted-foreground">Find where pallets and loose tiles are stored across the warehouses.</p>
        </div>
        <Button onClick={() => setDlg({ ...empty })} data-testid="add-stock-btn" className="gap-2"><Plus className="h-4 w-4" />Log stock</Button>
      </div>

      <div className="mt-5 flex flex-col gap-2 sm:flex-row">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search product, SKU or bay…" className="pl-9" data-testid="stock-search" />
        </div>
        <Select value={wh} onValueChange={setWh}>
          <SelectTrigger className="w-full sm:w-56" data-testid="stock-wh-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All warehouses</SelectItem>
            {whs.map((w) => <SelectItem key={w.code} value={w.code}>{w.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="mt-4 space-y-2" data-testid="stock-list">
        {loading ? <div className="py-16 text-center text-sm text-muted-foreground"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></div>
          : rows.length === 0 ? <div className="rounded-2xl border border-dashed border-border py-14 text-center text-sm text-muted-foreground">No stock logged yet. Tap "Log stock" to add the first location.</div>
          : rows.map((r) => (
            <div key={r.id} data-testid={`stock-row-${r.id}`} className="flex items-start justify-between gap-3 rounded-2xl border border-slate-100 bg-white p-4">
              <div className="flex min-w-0 gap-3">
                {r.photos?.length ? <StockPhoto placementId={r.id} photo={r.photos[0]} /> : null}
                <div className="min-w-0">
                <div className="font-display text-base font-bold text-ink">{r.product_name}{r.qty ? <span className="ml-2 text-sm font-normal text-muted-foreground">× {r.qty}</span> : null}</div>
                {r.sku && (r.inflow_product_id
                  ? <StockLevelBadge sku={r.sku} productId={r.inflow_product_id} />
                  : <div className="text-xs text-muted-foreground">{r.sku}</div>)}
                <div className="mt-1.5 flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-bold text-primary"><MapPin className="h-3 w-3" />{r.warehouse_name}</span>
                  <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 font-mono text-[11px] font-bold text-ink">{r.location}</span>
                  <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold" style={{ background: r.is_loose ? "#F5A62322" : "#05966922", color: r.is_loose ? "#B4791A" : "#059669" }}>{r.is_loose ? "Loose" : "Racked pallet"}</span>
                </div>
                {r.notes && <p className="mt-1.5 text-xs text-muted-foreground">{r.notes}</p>}
                {r.updated_by_name && <p className="mt-1 text-[11px] text-muted-foreground">Last moved by {r.updated_by_name}</p>}
                </div>
              </div>
              <div className="flex shrink-0 gap-1">
                <button onClick={() => setDlg({ ...r, sku: r.sku || "", qty: r.qty || "", notes: r.notes || "", photos: r.photos || [] })} data-testid={`stock-edit-${r.id}`} className="grid h-9 w-9 place-items-center rounded-lg text-muted-foreground hover:bg-accent hover:text-ink"><Pencil className="h-4 w-4" /></button>
                <button onClick={() => del(r)} data-testid={`stock-del-${r.id}`} className="grid h-9 w-9 place-items-center rounded-lg text-muted-foreground hover:bg-red-50 hover:text-error"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
      </div>

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className="max-w-md w-[calc(100%-1.5rem)] max-h-[90vh] overflow-y-auto" data-testid="stock-dialog">
          {dlg && (
            <>
              <DialogHeader><DialogTitle className="flex items-center gap-2"><Boxes className="h-5 w-5 text-primary" />{dlg.id ? "Edit / move stock" : "Log stock location"}</DialogTitle>
                <DialogDescription className="text-xs">Record where a pallet or loose stock is stored so the team can find it fast.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div><Label className="mb-1 block text-xs">Warehouse</Label>
                  <Select value={dlg.warehouse_code} onValueChange={(v) => setDlg((d) => ({ ...d, warehouse_code: v, location: "" }))}>
                    <SelectTrigger className="h-11" data-testid="stock-warehouse"><SelectValue /></SelectTrigger>
                    <SelectContent>{whs.map((w) => <SelectItem key={w.code} value={w.code}>{w.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5" data-testid="stock-type-toggle">
                  <button type="button" onClick={() => setDlg((d) => ({ ...d, is_loose: false, location: "" }))} className={`rounded-md px-4 py-2.5 text-xs font-semibold ${!dlg.is_loose ? "bg-primary text-white" : "text-muted-foreground"}`}>Racked pallet</button>
                  <button type="button" onClick={() => setDlg((d) => ({ ...d, is_loose: true, location: "" }))} className={`rounded-md px-4 py-2.5 text-xs font-semibold ${dlg.is_loose ? "bg-primary text-white" : "text-muted-foreground"}`}>Loose</button>
                </div>
                <div><Label className="mb-1 block text-xs">{dlg.is_loose ? "Loose zone" : "Bay"}</Label>
                  <Select value={dlg.location} onValueChange={(v) => setDlg((d) => ({ ...d, location: v }))}>
                    <SelectTrigger className="h-11" data-testid="stock-location"><SelectValue placeholder={dlg.is_loose ? "Choose zone" : "Choose bay"} /></SelectTrigger>
                    <SelectContent className="max-h-64">{locOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="mb-1 block text-xs">Product</Label>
                  <InflowProductPicker
                    item={{ id: dlg.id || "new", name: dlg.product_name, inflow_product_name: dlg.inflow_product_name, inflow_product_id: dlg.inflow_product_id, sku: dlg.sku }}
                    onSelect={(patch) => setDlg((d) => ({ ...d, product_name: patch.name || d.product_name, sku: patch.sku || "", inflow_product_id: patch.inflow_product_id || null, inflow_product_name: patch.inflow_product_name || null }))}
                  />
                  <Input className="mt-2 h-11" value={dlg.product_name} onChange={(e) => setDlg((d) => ({ ...d, product_name: e.target.value, inflow_product_id: null, inflow_product_name: null }))} placeholder="Or type a product name" data-testid="stock-product" />
                  <p className="mt-1 text-[11px] text-muted-foreground">Search the inFlow catalogue to auto-fill the SKU, or type a product not in inFlow.</p>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label className="mb-1 block text-xs">SKU</Label><Input value={dlg.sku} onChange={(e) => setDlg((d) => ({ ...d, sku: e.target.value }))} placeholder="Auto-fills" className="h-11" data-testid="stock-sku" /></div>
                  <div><Label className="mb-1 block text-xs">Qty (optional)</Label><Input value={dlg.qty} onChange={(e) => setDlg((d) => ({ ...d, qty: e.target.value }))} className="h-11" data-testid="stock-qty" /></div>
                </div>
                <div><Label className="mb-1 block text-xs">Notes (optional)</Label><Input value={dlg.notes} onChange={(e) => setDlg((d) => ({ ...d, notes: e.target.value }))} placeholder="e.g. near roller door" className="h-11" data-testid="stock-notes" /></div>
                <div>
                  <Label className="mb-1 block text-xs">Photos</Label>
                  {dlg.id ? (
                    <div className="flex flex-wrap gap-2">
                      {(dlg.photos || []).map((p) => <StockPhoto key={p.id} placementId={dlg.id} photo={p} onDelete={deletePhoto} />)}
                      <button type="button" onClick={() => photoRef.current?.click()} data-testid="stock-add-photo"
                        className="grid h-16 w-16 shrink-0 place-items-center rounded-lg border-2 border-dashed border-slate-200 text-slate-400 transition-colors hover:border-primary/40 hover:text-primary">
                        <Camera className="h-5 w-5" />
                      </button>
                      <input ref={photoRef} type="file" accept="image/*" capture="environment" className="hidden" data-testid="stock-photo-input"
                        onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadPhoto(f); e.target.value = ""; }} />
                    </div>
                  ) : (
                    <p className="text-[11px] text-muted-foreground">Save this location first, then reopen it to add photos.</p>
                  )}
                </div>
                <div className="flex justify-end pt-1"><Button onClick={save} disabled={saving} data-testid="stock-save" className="gap-2">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Package className="h-4 w-4" />}{dlg.id ? "Save" : "Log stock"}</Button></div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
